import { OrgUserSearchParams } from '@cibo/core'
import { AcreageFilter, USCountyFilter, USStateFilter } from '@cibo/ui'
import FilterList from '@mui/icons-material/FilterList'
import {
  Autocomplete,
  Button,
  DialogActions,
  Divider,
  Fade,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Form, Formik } from 'formik'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOrganizationList } from '../../hooks'
import { Loading } from '../Loading'

export interface UserSearchFiltersProps {
  onUpdate: (values: OrgUserSearchParams, replace?: boolean) => void
  filterValues: OrgUserSearchParams
  availableFilters?: {
    includeInactive: boolean
    persona: boolean
    organization: boolean
    state: boolean
    county: boolean
    acres: boolean
  }
}

export const UserSearchFilters = ({
  availableFilters = {
    includeInactive: true,
    persona: true,
    organization: true,
    state: true,
    county: true,
    acres: true,
  },
  filterValues,
  onUpdate,
}: UserSearchFiltersProps) => {
  const { t } = useTranslation('@cibo/profile/PaginatedUsersTable')
  const anchorEl = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)
  const organizations = useOrganizationList()

  const handleToggleFilter = (event: React.MouseEvent<HTMLButtonElement>) =>
    setOpen(value => !value)

  const handleSubmit = (values: OrgUserSearchParams) => {
    setOpen(false)
    onUpdate(values)
  }

  if (!organizations.isFetched) {
    return <Loading />
  }

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <IconButton
        onClick={handleToggleFilter}
        size="large"
        aria-label={t('filters')}
        ref={anchorEl}
        component="button"
      >
        <FilterList />
      </IconButton>
      <Popover
        anchorEl={anchorEl.current}
        open={open}
        onClose={handleToggleFilter}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        TransitionComponent={Fade}
        sx={{ marginTop: 1 }}
        PaperProps={{ elevation: 1 }}
      >
        <Formik
          initialValues={{ isActive: true, ...filterValues }}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ resetForm, touched, errors, values, setFieldValue }) => (
            <Form>
              <Stack padding={2} spacing={1} minWidth="600px">
                <Stack divider={<Divider orientation="vertical" flexItem />} direction="row">
                  {(availableFilters.persona ||
                    availableFilters.organization ||
                    availableFilters.includeInactive) && (
                    <Stack padding={2} spacing={3} flex={1}>
                      <Typography>{t('users')}</Typography>
                      {!!availableFilters.persona && (
                        <FormControl component="fieldset">
                          <FormLabel component="legend">{t('persona')}</FormLabel>
                          <Autocomplete
                            multiple
                            limitTags={2}
                            options={['grower', 'admin', 'advisor']}
                            getOptionLabel={option => t(`persona_${option}`)}
                            renderInput={params => (
                              <TextField {...params} placeholder={t('allPersonas')} />
                            )}
                            onChange={(e, v) => setFieldValue('persona', v)}
                            value={
                              typeof values.persona === 'string'
                                ? [values.persona]
                                : values.persona || []
                            }
                            size="small"
                            fullWidth
                          />
                        </FormControl>
                      )}
                      {!!availableFilters.organization && !!organizations.data?.length && (
                        <FormControl>
                          <FormLabel component="legend">{t('growerNetwork')}</FormLabel>
                          <Autocomplete
                            autoHighlight
                            renderInput={params => (
                              <TextField
                                {...params}
                                placeholder={t('allOrganizations')}
                                error={touched['orgId'] && !!errors['orgId']}
                                helperText={errors['orgId']}
                                variant="outlined"
                              />
                            )}
                            options={organizations.data.map(({ id }) => id)}
                            getOptionLabel={(option: string) =>
                              organizations.data?.find(({ id }) => id === option)?.label || ''
                            }
                            onChange={(e, v) => setFieldValue('orgId', v)}
                            value={values.orgId}
                            size="small"
                            fullWidth
                          />
                        </FormControl>
                      )}
                      {!!availableFilters.includeInactive && (
                        <FormControl>
                          <RadioGroup
                            value={values.isActive}
                            onChange={(e, v) => setFieldValue('isActive', v === 'true')}
                          >
                            <FormControlLabel
                              value={true}
                              control={<Radio />}
                              label={`${t('active')}`}
                            />
                            <FormControlLabel
                              value={false}
                              control={<Radio />}
                              label={`${t('inactive')}`}
                            />
                          </RadioGroup>
                        </FormControl>
                      )}
                    </Stack>
                  )}
                  {(!!availableFilters.state ||
                    availableFilters.county ||
                    availableFilters.acres) && (
                    <Stack padding={2} spacing={3} flex={1}>
                      <Typography>{t('location')}</Typography>
                      {!!availableFilters.state && <USStateFilter />}
                      {!!availableFilters.county && <USCountyFilter />}
                      {!!availableFilters.acres && <AcreageFilter />}
                    </Stack>
                  )}
                </Stack>

                <DialogActions>
                  <Button
                    onClick={e => {
                      handleToggleFilter(e)
                      resetForm()
                    }}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={e => {
                      onUpdate({ isActive: true }, true)
                      setOpen(false)
                    }}
                  >
                    {t('reset')}
                  </Button>
                  <Button type="submit" variant="contained">
                    {t('update')}
                  </Button>
                </DialogActions>
              </Stack>
            </Form>
          )}
        </Formik>
      </Popover>
    </Stack>
  )
}
